import React from 'react';
import { getPendingFocusId, REVEAL_ATTRIBUTE, registerReveal, subscribePendingFocusId, unregisterReveal } from '../settings/reveal';

/**
 * The pending `?focus=` id, or undefined once it has been dismissed or was never set.
 *
 * Reads the shared store in `settings/reveal.ts`, NOT `useLocation()` - dismissing a focus clears the
 * URL with `window.history.replaceState`, which the router never sees, so a hook based on
 * `useLocation()` alone would keep reporting the dismissed id as still pending. `useSettingFocus`
 * keeps the store in step with both real navigations and dismissal; this just subscribes to it.
 */
export const useSettingFocusId = (): string | undefined => {
    const [id, setId] = React.useState(getPendingFocusId);
    React.useEffect(() => subscribePendingFocusId(() => setId(getPendingFocusId())), []);
    return id;
};

/**
 * True while a setting deep link is pending and has not been dismissed yet.
 *
 * Collapsible containers use this to keep rendering their children (hidden) so a deep link can find
 * a control inside them. Outside of a deep link it is false, so nothing changes about how the app
 * renders normally.
 */
export const useSettingFocusPending = (): boolean => !!useSettingFocusId();

export interface ISettingRevealState {
    /** Spread onto the container's root element. */
    revealProps: { ref: React.RefCallback<HTMLDivElement> } & Record<string, unknown>;
    /**
     * Render children even though collapsed, so a deep link can find them. They must be rendered
     * hidden - see `hiddenStyle`.
     */
    renderCollapsedChildren: boolean;
    hiddenStyle: React.CSSProperties;
}

/**
 * Lets a collapsible container be opened by a settings deep link.
 *
 * `expanded` is the container's current state and `expand` opens it. The container keeps owning its
 * own state; this only registers a way to ask it to open.
 */
export const useSettingReveal = (expanded: boolean, expand: () => void): ISettingRevealState => {
    const focusPending = useSettingFocusPending();
    const elementRef = React.useRef<HTMLDivElement | null>(null);
    const expandRef = React.useRef(expand);
    expandRef.current = expand;

    const ref = React.useCallback<React.RefCallback<HTMLDivElement>>((node) => {
        if (elementRef.current) unregisterReveal(elementRef.current);
        elementRef.current = node;
        registerReveal(node, () => expandRef.current());
    }, []);

    React.useEffect(() => () => unregisterReveal(elementRef.current), []);

    return {
        revealProps: { ref, [REVEAL_ATTRIBUTE]: true },
        renderCollapsedChildren: focusPending && !expanded,
        // Present in the DOM so it can be found, but taking no space and not read out.
        hiddenStyle: { display: 'none' },
    };
};

export default useSettingReveal;
